import { Modal, Button, Table, Tabs, Tag } from 'rsuite';
import CopyButton from './CopyButton';

const { Column, HeaderCell, Cell } = Table;

const formatMemory = (mb) => {
  if (!mb) return '-';
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
};

const formatPrice = (price) => {
  if (!price) return '-';
  return `$${parseFloat(price).toFixed(2)}`;
};

const availabilityTag = (available) => {
  const v = typeof available === 'string' ? available.toLowerCase() : (available ? 'yes' : 'no');
  if (v === 'maybe') return <Tag color="orange">Maybe</Tag>;
  if (v === 'yes') return <Tag color="green">Yes</Tag>;
  return <Tag color="red">No</Tag>;
};

// Friendly rendering of a single property value in the details table.
const renderValue = (property, value) => {
  if (property === 'available') return availabilityTag(value);
  if (property === 'memory_mb') return formatMemory(value);
  if (property === 'price_hourly' || property === 'price_monthly') return formatPrice(value);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object' && value !== null) {
    return (
      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return String(value);
};

/**
 * Read-only dialog showing every property of a GPU instance size, plus a
 * dedicated, copyable list of suggested Docker images.
 */
function InstanceDetailsDialog({ show, onClose, instance }) {
  const data = instance || {};
  const images = Array.isArray(data.suggested_images) ? data.suggested_images : [];

  const rows = Object.keys(data)
    .filter((key) => key !== 'suggested_images')
    .filter((key) => {
      const v = data[key];
      if (v === null || v === undefined || v === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    })
    .map((key) => ({ property: key, value: data[key] }));

  return (
    <Modal open={show} onClose={onClose} size="lg">
      <Modal.Header>
        <Modal.Title>Instance: {data.slug || ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs defaultActiveKey="details">
          <Tabs.Tab eventKey="details" title="Details">
            <div style={{ marginTop: 15 }}>
              <Table data={rows} autoHeight>
                <Column width={200}>
                  <HeaderCell>Property</HeaderCell>
                  <Cell dataKey="property" />
                </Column>
                <Column flexGrow={1}>
                  <HeaderCell>Value</HeaderCell>
                  <Cell>{(rowData) => renderValue(rowData.property, rowData.value)}</Cell>
                </Column>
              </Table>

              <div style={{ marginTop: 24 }}>
                <h6 style={{ marginBottom: 10 }}>Suggested images</h6>
                {images.length > 0 ? (
                  images.map((img, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 0',
                        fontFamily: 'monospace',
                        fontSize: 12,
                      }}
                    >
                      <span style={{ wordBreak: 'break-all' }}>{img}</span>
                      <div onClick={(e) => e.stopPropagation()}>
                        <CopyButton
                          text={img}
                          size="xs"
                          successMessage="Image copied to clipboard"
                          errorMessage="Failed to copy image"
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#aaa', fontStyle: 'italic' }}>No suggested images.</div>
                )}
              </div>
            </div>
          </Tabs.Tab>
          <Tabs.Tab eventKey="json" title="JSON">
            <div style={{ marginTop: 15 }}>
              <pre
                style={{
                  background: '#2a2a2a',
                  color: '#fff',
                  padding: 15,
                  borderRadius: 4,
                  fontSize: 12,
                  maxHeight: 500,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0,
                }}
              >
                {JSON.stringify(data, null, 2)}
              </pre>
            </div>
          </Tabs.Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} appearance="subtle">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default InstanceDetailsDialog;
